// src/layers/imagery/collectSummary.js
//
// What one COLLECT is, for the IMAGERY tab's orientation list: date, how much
// ground, and how many separate pieces that ground is in.
//
// ── ALL OF IT IS ALREADY IN THE CLIENT ──────────────────────────────────────
//
// groupFootprintsIntoSeries carries `geometry` per acquisition -- the dissolved
// fine footprint, kept deliberately rather than discarded after computing bounds
// (see its note). So this needs no request, and the tab stays as cheap to open
// as the strip that replaced its selection role.
//
// @turf/area was already a dependency; nothing new is pulled in.

import area from "@turf/area";

/**
 * How many disjoint pieces this collect's coverage is in.
 *
 * ── DO NOT REACH FOR "THE FIRST PART" ───────────────────────────────────────
 *
 * MultiPolygon member order is arbitrary -- it is whatever the dissolve emitted,
 * not a ranking. Measured 2026-09-07 on 2025_12_13: ST_GeometryN(geom, 1) is
 * 79 km2 while the LARGEST of its 19 parts is 2,183 km2. Anything that takes
 * coordinates[0] to mean "the main area" gets a near-smallest one and looks
 * plausible while doing it.
 *
 * This matters for the fly-to work, which is a later commit: the target has to
 * be chosen by measuring the parts, never by taking the first.
 */
export function partCount(geometry) {
  if (!geometry) return 0;
  if (geometry.type === "Polygon") return 1;
  if (geometry.type === "MultiPolygon") return geometry.coordinates?.length ?? 0;
  return 0;
}

/** Square kilometres of actual coverage, from the dissolved fine footprint. */
export function areaKm2(geometry) {
  if (!geometry) return null;
  try {
    return area({ type: "Feature", geometry, properties: {} }) / 1e6;
  } catch {
    // A malformed geometry is a null area, not a crash: the row still renders
    // its date and county line, which is most of what the list is for.
    return null;
  }
}

/**
 * One flat row per collect, newest first.
 *
 * Flattens series -> acquisitions, because a COLLECT is what a user orients by
 * ("is there imagery from December here"), while a series is a product line.
 * The old list was series-first and made the date a second-level detail.
 */
export function collectRows(series) {
  const rows = [];
  for (const s of series ?? []) {
    for (const acq of s.acquisitions ?? []) {
      rows.push({
        key: `${s.series_key}::${acq.acquisition_date}`,
        seriesKey: s.series_key,
        seriesName: s.display_name ?? s.series_key,
        date: acq.acquisition_date,
        // THE ACQUISITION OBJECT ITSELF, not just its date. CountyDetailFlyout
        // takes `selectedAcquisitions` and calls rollUpCounties() on it, so it
        // needs objects carrying `.counties`; passing dates would roll up
        // nothing and the flyout would silently fall back to the series-level
        // summary, showing the wrong collect's coverage while looking correct.
        acq,
        geometry: acq.geometry ?? null,
        bounds: acq.bounds ?? null,
        areaKm2: areaKm2(acq.geometry),
        parts: partCount(acq.geometry),
        // PER-COLLECT COUNTIES, falling back to the series roll-up.
        //
        // groupFootprintsIntoSeries fills counties at BOTH levels: per
        // acquisition from the feature, and a series-level roll-up taking max
        // coverage per county across acquisitions. A per-collect row wants the
        // acquisition's own -- the roll-up would credit December with September's
        // best county coverage and vice versa.
        //
        // Shaped as { ...series, counties } so SeriesCoverageLine and
        // CountyDetailFlyout take it unchanged: both read `series.counties`, and
        // the flyout also reads the series' acquisitions, which must stay whole.
        series: { ...s, counties: acq.counties ?? s.counties ?? null },
      });
    }
  }
  // Newest first. Dates are YYYY_MM_DD, which sorts lexicographically as it
  // sorts chronologically -- no parsing, and no timezone to get wrong.
  return rows.sort((a, b) => String(b.date).localeCompare(String(a.date)));
}
